/* eslint-disable no-undef */
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  LinearProgress,
  Chip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Close,
  AutoAwesome,
  Code
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useCurrentUser, type UserInfo } from '../../hooks/useApi';
import { API_URL } from '../../utils/config';

interface LLMModel {
  id: number;
  provider: string;
  modelName: string;
  alias?: string;
  family: string;
  release: string;
}

interface MarketPromptOption {
  id: number;
  market_id: number | null;
  prompt_key: string;
  model_id: number;
  system_prompt: string | null;
  prompt: string;
  temperature: number;
  max_tokens: number;
  pdf_characters: number;
  description: string | null;
  created_by: string | null;
  created_at: string | null;
  created_by_email: string | null;
  prompt_type: 'Custom Prompt' | 'Default Prompt';
}

interface RegenerateDescriptionDialogProps {
  open: boolean;
  onClose: () => void;
  marketId: number;
  marketName: string;
  onSuccess: () => void;
}

export const RegenerateDescriptionDialog: React.FC<RegenerateDescriptionDialogProps> = ({
  open,
  onClose,
  marketId,
  marketName,
  onSuccess
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { data: currentUser } = useCurrentUser() as { data: UserInfo | null | undefined };
  const queryClient = useQueryClient();
  const [selectedPromptId, setSelectedPromptId] = useState<number | string>('');
  const [formData, setFormData] = useState({
    prompt: '',
    systemPrompt: '',
    modelId: undefined as number | undefined,
    temperature: 0.7,
    maxTokens: 2000
  });
  const [originalPromptData, setOriginalPromptData] = useState<{
    prompt: string;
    systemPrompt: string;
    pdfCharacters: number;
    description: string | null;
  } | null>(null);
  const [savePromptChanges, setSavePromptChanges] = useState(true);
  const [promptDescriptionDialogOpen, setPromptDescriptionDialogOpen] = useState(false);
  const [promptDescription, setPromptDescription] = useState('');
  const [operationId, setOperationId] = useState<string | null>(null);
  const [operationStatus, setOperationStatus] = useState<string>('');
  const [operationProgress, setOperationProgress] = useState<string>('');
  const [operationError, setOperationError] = useState<string>('');
  const [generatedDescription, setGeneratedDescription] = useState<string>('');
  const [llmMetrics, setLlmMetrics] = useState<{
    model?: string;
    promptTokens?: number;
    completionTokens?: number;
    totalTokens?: number;
    estimatedCostUsd?: number;
  } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);

  // Fetch market-specific prompts
  const { data: marketPromptsData, isLoading: marketPromptsLoading, refetch: refetchPrompts } = useQuery({
    queryKey: ['market-prompts', marketId],
    queryFn: async () => {
      const response = await authenticatedFetch(
        `${API_URL}/api/marketcompass/admin/markets/${marketId}/prompts?promptKey=market_description_from_raw_data`
      );
      if (!response || !response.ok) {
        throw new Error('Failed to fetch market prompts');
      }
      const result = await response.json();
      return result.data || [];
    },
    enabled: open,
    refetchOnMount: true,  // Always refetch when component mounts
    staleTime: 0  // Consider data immediately stale
  });

  // Fetch available models
  const { data: modelsData, isLoading: modelsLoading } = useQuery({
    queryKey: ['models'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/models`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch models');
      }
      return await response.json();
    },
    enabled: open
  });

  const models = modelsData?.data?.models || [];
  const groupedModels = modelsData?.data?.grouped || {};

  // Handle prompt selection
  const handlePromptSelection = (promptId: number) => {
    setSelectedPromptId(promptId);
    const selectedPrompt = marketPromptsData?.find((p: MarketPromptOption) => p.id === promptId);
    if (selectedPrompt) {
      setFormData({
        prompt: selectedPrompt.prompt || '',
        systemPrompt: selectedPrompt.system_prompt || '',
        modelId: selectedPrompt.model_id || undefined,
        temperature: selectedPrompt.temperature || 0.7,
        maxTokens: selectedPrompt.max_tokens || 2000
      });
      // Store original prompt data for comparison
      setOriginalPromptData({
        prompt: selectedPrompt.prompt || '',
        systemPrompt: selectedPrompt.system_prompt || '',
        pdfCharacters: selectedPrompt.pdf_characters || 100000,
        description: selectedPrompt.description || null
      });
    }
  };

  // Refetch prompts when dialog opens
  useEffect(() => {
    if (open) {
      refetchPrompts();
    }
  }, [open, refetchPrompts]);

  // Initialize form with first prompt's data (but don't select it in dropdown)
  useEffect(() => {
    if (marketPromptsData && marketPromptsData.length > 0 && !marketPromptsLoading && formData.prompt === '') {
      // Get the first prompt (custom prompts come first, then default)
      const firstPrompt = marketPromptsData[0];

      // Populate form fields with the first prompt's data
      setFormData({
        prompt: firstPrompt.prompt || '',
        systemPrompt: firstPrompt.system_prompt || '',
        modelId: firstPrompt.model_id || undefined,
        temperature: firstPrompt.temperature || 0.7,
        maxTokens: firstPrompt.max_tokens || 2000
      });

      // Store as original for change detection
      setOriginalPromptData({
        prompt: firstPrompt.prompt || '',
        systemPrompt: firstPrompt.system_prompt || '',
        pdfCharacters: firstPrompt.pdf_characters || 100000,
        description: firstPrompt.description || null
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketPromptsData, marketPromptsLoading]);

  // Check if prompts have been modified
  const hasPromptChanges = originalPromptData && (
    formData.prompt !== originalPromptData.prompt ||
    formData.systemPrompt !== originalPromptData.systemPrompt
  );

  // Save description mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      // Step 1: Save custom prompt if changes were made and checkbox is checked
      if (hasPromptChanges && savePromptChanges && originalPromptData) {
        const promptResponse = await authenticatedFetch(
          `${API_URL}/api/marketcompass/admin/markets/${marketId}/prompts/save`,
          {
            method: 'POST',
            body: JSON.stringify({
              promptKey: 'market_description_from_raw_data',
              modelId: formData.modelId,
              systemPrompt: formData.systemPrompt,
              prompt: formData.prompt,
              temperature: formData.temperature,
              maxTokens: formData.maxTokens,
              pdfCharacters: originalPromptData.pdfCharacters,
              description: promptDescription
            })
          }
        );

        if (!promptResponse || !promptResponse.ok) {
          const errorData = await promptResponse?.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to save custom prompt');
        }
      }

      // Step 2: Save market description
      const response = await authenticatedFetch(
        `${API_URL}/api/marketcompass/admin/markets/${marketId}/update`,
        {
          method: 'PUT',
          body: JSON.stringify({
            description: generatedDescription
          })
        }
      );

      if (!response || !response.ok) {
        const errorData = await response?.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save description');
      }

      return await response.json();
    },
    onSuccess: async () => {
      // Invalidate the prompts query to refresh the dropdown with new custom prompts
      await queryClient.invalidateQueries({ queryKey: ['market-prompts', marketId] });
      onSuccess();
      handleClose();
    },
    onError: (error: any) => {
      setOperationError(error.message || 'Failed to save description');
    }
  });

  // Generate description mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      // Find the selected model to get provider and model name
      const selectedModel = models.find((m: LLMModel) => m.id === formData.modelId);
      if (!selectedModel) {
        throw new Error('Please select a model');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/marketcompass/admin/markets/${marketId}/generate-description`,
        {
          method: 'POST',
          body: JSON.stringify({
            prompt: {
              prompt: formData.prompt,
              systemPrompt: formData.systemPrompt || undefined,
              provider: selectedModel.provider,
              model: selectedModel.modelName,
              temperature: formData.temperature,
              maxTokens: formData.maxTokens
            }
          })
        }
      );

      if (!response || !response.ok) {
        const errorData = await response?.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to generate description');
      }

      const result = await response.json();
      return result;
    },
    onSuccess: (data) => {
      if (data.operationId) {
        setOperationId(data.operationId);
        setOperationStatus(data.status || 'pending');
        setOperationProgress('Initializing market description generation...');
        // Start polling for operation status
        startPolling(data.operationId);
      }
    },
    onError: (error: any) => {
      setErrors({
        submit: error.message || 'Failed to generate description'
      });
      setOperationError(error.message || 'Failed to generate description');
    }
  });

  // Stop polling function
  const stopPolling = () => {
    console.log('Stopping polling...');
    isPollingRef.current = false;
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      console.log('Polling interval cleared');
    }
  };

  // Poll for operation status
  const startPolling = (opId: string) => {
    // Stop any existing polling first
    stopPolling();
    
    // Set polling flag
    isPollingRef.current = true;
    console.log('Starting polling for operation:', opId);

    const pollOperation = async () => {
      // Check if we should continue polling
      if (!isPollingRef.current) {
        console.log('Polling flag is false, stopping...');
        return;
      }

      try {
        const response = await authenticatedFetch(
          `${API_URL}/api/marketcompass/admin/operations/${opId}`
        );
        
        if (!response || !response.ok) {
          stopPolling();
          setOperationError('Failed to check operation status');
          return;
        }

        const data = await response.json();
        
        if (data.success) {
          const currentStatus = data.status || 'pending';
          const currentProgress = data.progress || '';
          
          // Log for debugging
          console.log('Operation Status:', currentStatus, 'Progress:', currentProgress);
          
          // Update state
          setOperationStatus(currentStatus);
          setOperationProgress(currentProgress);
          
          // Check if operation is complete or failed
          if (currentStatus === 'completed' || currentStatus === 'failed') {
            // Stop polling immediately
            stopPolling();

            if (currentStatus === 'completed') {
              // Extract generated description from operation result
              if (data.result && data.result.marketSummary) {
                setGeneratedDescription(data.result.marketSummary);
                // Extract LLM metrics if available
                if (data.result.llmMetrics) {
                  setLlmMetrics(data.result.llmMetrics);
                }
              } else {
                setOperationError('Generated description not found in response');
              }
            } else if (currentStatus === 'failed') {
              setOperationError(data.error || 'Generation failed');
            }
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
        stopPolling();
        setOperationError('Failed to check operation status');
      }
    };

    // Start polling with interval
    const startInterval = () => {
      if (!isPollingRef.current) {
        console.log('Polling cancelled before starting');
        return;
      }
      
      pollingIntervalRef.current = setInterval(() => {
        if (!isPollingRef.current) {
          stopPolling();
          return;
        }
        pollOperation();
      }, 2000);
    };

    // Do an immediate check
    pollOperation();
    // Then start the interval
    startInterval();
  };

  // Clean up polling interval when component unmounts
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.prompt) {
      newErrors.prompt = 'Prompt text is required';
    }
    if (!formData.modelId) {
      newErrors.modelId = 'Model selection is required';
    }
    if (formData.temperature < 0 || formData.temperature > 1) {
      newErrors.temperature = 'Temperature must be between 0 and 1';
    }
    if (formData.maxTokens < 1) {
      newErrors.maxTokens = 'Max tokens must be positive';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGenerate = () => {
    if (validateForm()) {
      setOperationError('');
      generateMutation.mutate();
    }
  };

  const handleClose = () => {
    // Stop polling
    stopPolling();
    // Reset state
    setSelectedPromptId('');
    setOperationId(null);
    setOperationStatus('');
    setOperationProgress('');
    setOperationError('');
    setGeneratedDescription('');
    setLlmMetrics(null);
    setSavePromptChanges(true);
    setOriginalPromptData(null);
    setPromptDescription('');
    setPromptDescriptionDialogOpen(false);
    setErrors({});
    onClose();
  };

  const handleSave = () => {
    // If saving prompt changes, ask for a description first
    if (hasPromptChanges && savePromptChanges) {
      const defaultDescription = `New Prompt by ${currentUser?.email || 'user'}`;
      setPromptDescription(defaultDescription);
      setPromptDescriptionDialogOpen(true);
    } else {
      // No prompt changes, just save the description
      saveMutation.mutate();
    }
  };

  const handleConfirmPromptDescription = () => {
    setPromptDescriptionDialogOpen(false);
    saveMutation.mutate();
  };

  const handleCancel = () => {
    setGeneratedDescription('');
    setLlmMetrics(null);
    setOperationId(null);
    setOperationStatus('');
    setSavePromptChanges(true);
  };

  const isProcessing = operationStatus === 'pending' || operationStatus === 'processing';

  return (
    <>
    <Dialog
      open={open}
      onClose={isProcessing ? undefined : handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '85vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesome color="primary" />
            <Typography variant="h5">
              Regenerate Market Description
            </Typography>
          </Box>
          <IconButton onClick={handleClose} edge="end" disabled={isProcessing}>
            <Close />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Customize the AI prompt to regenerate the description for {marketName}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Generated Description Review */}
        {generatedDescription && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            p: 3
          }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesome />
              Generated Description
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Review and edit the AI-generated description below, then click Save to apply it to the market.
            </Typography>

            {/* LLM Metrics Display */}
            {llmMetrics && (
              <Box sx={{
                mt: 2,
                p: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  LLM Usage Metrics
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {llmMetrics.model && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Model
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {llmMetrics.model}
                      </Typography>
                    </Box>
                  )}
                  {llmMetrics.promptTokens !== undefined && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Prompt Tokens
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {llmMetrics.promptTokens.toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                  {llmMetrics.completionTokens !== undefined && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Completion Tokens
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {llmMetrics.completionTokens.toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                  {llmMetrics.totalTokens !== undefined && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Total Tokens
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {llmMetrics.totalTokens.toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                  {llmMetrics.estimatedCostUsd !== undefined && llmMetrics.estimatedCostUsd !== null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Estimated Cost
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                        ${llmMetrics.estimatedCostUsd.toFixed(6)} USD
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            <Box sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              mt: 2,
              mb: 2,
              minHeight: 0  // Important for flexbox overflow
            }}>
              <TextField
                fullWidth
                multiline
                value={generatedDescription}
                onChange={(e) => setGeneratedDescription(e.target.value)}
                variant="outlined"
                sx={{
                  flexGrow: 1,
                  '& .MuiInputBase-root': {
                    height: '100%',
                    alignItems: 'flex-start'
                  },
                  '& .MuiInputBase-input': {
                    height: '100% !important',
                    overflow: 'auto !important'
                  }
                }}
              />
            </Box>
            {hasPromptChanges && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={savePromptChanges}
                    onChange={(e) => setSavePromptChanges(e.target.checked)}
                    disabled={saveMutation.isPending}
                  />
                }
                label="Save Prompt Changes as New Prompt"
                sx={{ mb: 2 }}
              />
            )}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={saveMutation.isPending}
                sx={{
                  borderColor: '#003F9F',
                  color: '#003F9F',
                  '&:hover': {
                    borderColor: '#003F9F',
                    backgroundColor: 'rgba(0, 63, 159, 0.04)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saveMutation.isPending || !generatedDescription.trim()}
                startIcon={saveMutation.isPending ? <CircularProgress size={20} /> : undefined}
                sx={{
                  backgroundColor: '#32373C',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: '#1f2327'
                  }
                }}
              >
                {saveMutation.isPending ? 'Saving...' : 'Save Description'}
              </Button>
            </Box>
            {operationError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {operationError}
              </Alert>
            )}
          </Box>
        )}

        {/* Operation Status Bar */}
        {operationId && !generatedDescription && (
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Chip
                label={
                  operationStatus === 'pending' ? 'Pending' :
                  operationStatus === 'processing' ? 'Processing' :
                  operationStatus === 'completed' ? 'Completed' :
                  operationStatus === 'failed' ? 'Failed' :
                  operationStatus
                }
                color={
                  operationStatus === 'completed' ? 'success' :
                  operationStatus === 'failed' ? 'error' :
                  operationStatus === 'processing' ? 'info' :
                  'default'
                }
                size="small"
                icon={
                  operationStatus === 'processing' || operationStatus === 'pending' ? 
                    <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 
                    undefined
                }
              />
              {operationProgress && (
                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                  {operationProgress}
                </Typography>
              )}
            </Box>
            {isProcessing && (
              <Box sx={{ mb: 1 }}>
                <LinearProgress variant="indeterminate" />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {operationStatus === 'processing' ? 'Generating market description with AI...' : 'Initializing...'}
                </Typography>
              </Box>
            )}
            {operationError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {operationError}
              </Alert>
            )}
            {operationStatus === 'completed' && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Description generated successfully! Refreshing page...
              </Alert>
            )}
          </Box>
        )}

        {/* Main Form - Hidden when showing generated description */}
        {!generatedDescription && (
          <Box sx={{ display: 'flex', height: '100%' }}>
            {/* Main Content Area */}
            <Box sx={{ flex: 1, p: 3 }}>
            <Grid container spacing={2}>
              {/* Select Prompt Dropdown - Simple Implementation */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="select-prompt-label">Select Prompt</InputLabel>
                  <Select
                    labelId="select-prompt-label"
                    value={selectedPromptId || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val && val !== '') {
                        handlePromptSelection(Number(val));
                      }
                    }}
                    label="Select Prompt"
                    disabled={marketPromptsLoading || isProcessing}
                  >
                    <MenuItem value="">
                      <em>None - Use current values</em>
                    </MenuItem>

                    {marketPromptsData && marketPromptsData.map((prompt: MarketPromptOption) => (
                      <MenuItem key={prompt.id} value={prompt.id}>
                        {prompt.prompt_type === 'Custom Prompt'
                          ? `${prompt.description || 'Custom Prompt'} (${new Date(prompt.created_at!).toLocaleDateString()})`
                          : 'Default System Prompt'
                        }
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* System Prompt */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  System Prompt (Optional)
                </Typography>
                <Box sx={{ height: 100, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Editor
                    height="100px"
                    defaultLanguage="plaintext"
                    value={formData.systemPrompt}
                    onChange={(value) => setFormData({ ...formData, systemPrompt: value || '' })}
                    options={{
                      minimap: { enabled: false },
                      lineNumbers: 'off',
                      wordWrap: 'on',
                      fontSize: 14,
                      scrollBeyondLastLine: false,
                      scrollbar: {
                        vertical: 'auto',
                        horizontal: 'auto'
                      }
                    }}
                  />
                </Box>
              </Grid>

              {/* Main Prompt */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Prompt Template *
                </Typography>
                {errors.prompt && (
                  <Alert severity="error" sx={{ mb: 1 }}>{errors.prompt}</Alert>
                )}
                <Box sx={{ height: 350, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Editor
                    height="350px"
                    defaultLanguage="plaintext"
                    value={formData.prompt}
                    onChange={(value) => setFormData({ ...formData, prompt: value || '' })}
                    options={{
                      minimap: { enabled: false },
                      wordWrap: 'on',
                      fontSize: 14,
                      scrollBeyondLastLine: false,
                      scrollbar: {
                        vertical: 'auto',
                        horizontal: 'auto'
                      }
                    }}
                    loading={marketPromptsLoading}
                  />
                </Box>
              </Grid>

              {/* Template Variables Info */}
              <Grid item xs={12}>
                <Alert severity="info" icon={<Code />}>
                  <Typography variant="subtitle2" gutterBottom>
                    Available Template Variables:
                  </Typography>
                  <Typography variant="body2" component="div">
                    • <code>{'{{marketName}}'}</code> - Market name<br />
                    • <code>{'{{country}}'}</code> - Country name<br />
                    • <code>{'{{currentDate}}'}</code> - Current date (YYYY-MM-DD)<br />
                    • <code>{'{{csvData}}'}</code> - Market analytics data in CSV format
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Box>

          {/* Sidebar */}
          <Box sx={{ width: 350, borderLeft: 1, borderColor: 'divider', p: 2, overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Model Settings
            </Typography>

            {/* Model Selection */}
            <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.modelId}>
              <InputLabel>Model *</InputLabel>
              <Select
                value={formData.modelId !== undefined && formData.modelId !== null ? formData.modelId : ''}
                onChange={(e) => setFormData({ ...formData, modelId: Number(e.target.value) })}
                label="Model *"
                disabled={modelsLoading || isProcessing}
              >
                {modelsLoading ? (
                  <MenuItem value="">Loading models...</MenuItem>
                ) : (
                  Object.entries(groupedModels).map(([provider, families]) => [
                    <MenuItem key={`header-${provider}`} disabled>
                      <Typography variant="overline" sx={{ color: 'text.primary', fontWeight: 600 }}>{provider}</Typography>
                    </MenuItem>,
                    ...Object.entries(families as Record<string, LLMModel[]>).map(([family, familyModels]) =>
                      familyModels.map((model) => (
                        <MenuItem key={model.id} value={model.id} sx={{ pl: 3 }}>
                          <Typography variant="body2">{model.modelName}</Typography>
                        </MenuItem>
                      ))
                    )
                  ])
                )}
              </Select>
              {errors.modelId && (
                <Typography variant="caption" color="error">{errors.modelId}</Typography>
              )}
            </FormControl>

            {/* Temperature */}
            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom>
                Temperature: {formData.temperature}
              </Typography>
              <Slider
                value={formData.temperature}
                onChange={(_, value) => setFormData({ ...formData, temperature: value as number })}
                min={0}
                max={1}
                step={0.1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.5, label: '0.5' },
                  { value: 1, label: '1' }
                ]}
                disabled={isProcessing}
              />
              <Typography variant="caption" color="text.secondary">
                Lower = more focused, Higher = more creative
              </Typography>
            </Box>

            {/* Max Tokens */}
            <TextField
              fullWidth
              type="number"
              label="Max Tokens"
              value={formData.maxTokens}
              onChange={(e) => setFormData({ ...formData, maxTokens: parseInt(e.target.value) || 0 })}
              error={!!errors.maxTokens}
              helperText={errors.maxTokens || 'Maximum response length'}
              disabled={isProcessing}
              sx={{ mb: 2 }}
            />

            {errors.submit && (
              <Alert severity="error" sx={{ mt: 2 }}>{errors.submit}</Alert>
            )}
          </Box>
        </Box>
        )}
      </DialogContent>

      {!generatedDescription && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClose}
            disabled={isProcessing}
            sx={{
              backgroundColor: 'transparent',
              color: '#003F9F',
              border: '2px solid #003F9F',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: 'rgba(0, 63, 159, 0.04)',
                border: '2px solid #003F9F'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={isProcessing ? <CircularProgress size={20} /> : <AutoAwesome />}
            onClick={handleGenerate}
            disabled={isProcessing || marketPromptsLoading}
            sx={{
              backgroundColor: '#32373C',
              color: '#FFFFFF',
              padding: '0.75em 1.5em',
              borderRadius: '3px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              '&:hover': {
                backgroundColor: '#1f2327'
              }
            }}
          >
            {isProcessing ? 'Generating...' : 'Generate Description'}
          </Button>
        </DialogActions>
      )}
    </Dialog>

    {/* Prompt Description Dialog */}
    <Dialog
      open={promptDescriptionDialogOpen}
      onClose={() => setPromptDescriptionDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Prompt Description</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
          Enter a description for this custom prompt to help you identify it later.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Description"
          value={promptDescription}
          onChange={(e) => setPromptDescription(e.target.value)}
          variant="outlined"
          multiline
          rows={3}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPromptDescriptionDialogOpen(false)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmPromptDescription}
          disabled={!promptDescription.trim()}
          sx={{
            backgroundColor: '#32373C',
            '&:hover': {
              backgroundColor: '#1f2327'
            }
          }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};